import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/shared/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useCompanyProfile } from '../hooks/useCompanyProfile';
import { WorkshopToolForm } from '../components/WorkshopToolForm';
import { MachineManagerPanel } from '../components/MachineManagerPanel';
import { ArticleManagerPanel } from '../components/ArticleManagerPanel';
import { MaterialManagerPanel } from '../components/MaterialManagerPanel';
import { MaterialLotManagerPanel } from '../components/MaterialLotManagerPanel';
import { ProcessManagerPanel } from '../components/ProcessManagerPanel';
import { Wrench, Settings2, PackageSearch, Layers, LayoutGrid, Boxes } from 'lucide-react';

export function WorkshopPage() {
  const http = useApiClient();
  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyName, companyAutoFilled } = useCompanyProfile();

  const breadcrumbItems = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    { label: t('traceability.workshop.hero.breadcrumb') },
  ];

  return (
    <PageLayout contentClassName="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          label={{ icon: Wrench, text: t('traceability.workshop.hero.badge') }}
          title={t('traceability.workshop.hero.title')}
          description={t('traceability.workshop.hero.description')}
        />

        <Tabs defaultValue="tool" className="flex w-full flex-col gap-5">
          <TabsList className="w-fit rounded-lg bg-muted p-1">
            <TabsTrigger value="tool" className="flex items-center gap-1.5 rounded-md">
              <Wrench className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.tool')}
            </TabsTrigger>
            <TabsTrigger value="machines" className="flex items-center gap-1.5 rounded-md">
              <Settings2 className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.machines')}
            </TabsTrigger>
            <TabsTrigger value="articles" className="flex items-center gap-1.5 rounded-md">
              <PackageSearch className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.articles')}
            </TabsTrigger>
            <TabsTrigger value="materials" className="flex items-center gap-1.5 rounded-md">
              <Layers className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.materials')}
            </TabsTrigger>
            <TabsTrigger value="lots" className="flex items-center gap-1.5 rounded-md">
              <Boxes className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.lots')}
            </TabsTrigger>
            <TabsTrigger value="processes" className="flex items-center gap-1.5 rounded-md">
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
              {t('traceability.workshop.tabs.processes')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tool" className="focus-visible:outline-none">
            <WorkshopToolForm companyName={companyName} companyAutoFilled={companyAutoFilled} />
          </TabsContent>

          <TabsContent value="machines" className="focus-visible:outline-none">
            <MachineManagerPanel companyName={companyName} traceabilityApiClient={traceabilityApiClient} />
          </TabsContent>

          <TabsContent value="articles" className="focus-visible:outline-none">
            <ArticleManagerPanel companyName={companyName} traceabilityApiClient={traceabilityApiClient} />
          </TabsContent>

          <TabsContent value="materials" className="focus-visible:outline-none">
            <MaterialManagerPanel companyName={companyName} traceabilityApiClient={traceabilityApiClient} />
          </TabsContent>

          <TabsContent value="lots" className="focus-visible:outline-none">
            <MaterialLotManagerPanel traceabilityApiClient={traceabilityApiClient} />
          </TabsContent>

          <TabsContent value="processes" className="focus-visible:outline-none">
            <ProcessManagerPanel companyName={companyName} traceabilityApiClient={traceabilityApiClient} />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}
